import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material'
import LoadingIndicator from '../../common/LoadingIndicator'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBan, faCopy } from '@fortawesome/free-solid-svg-icons'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'

export function CRMTable({
  columnConfig,
  sortPriority,
  loading,
  error,
  results,
  numberFmt,
  total,
  from,
  to,
  offset,
  limit,
  handlePrev,
  handleNext,
  onSendWhatsApp
}) {
  return (
    <div className='bg-white dark:bg-dk-surface rounded-xl shadow-sm w-full'>
      <TableContainer
        sx={{ maxHeight: 'calc(100vh - 4rem - 160px)' }}
        className='overflow-x-auto w-full'
      >
        <Table size='small' className='text-sm' sx={{ width: '100%' }}>
          <TableHead className='bg-gray-50 dark:bg-dk-raised'>
            <TableRow>
              {columnConfig.map(col => (
                <TableCell
                  key={col.key}
                  className='text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300 select-none'
                >
                  <div className='flex items-center gap-1'>
                    <span>{col.label}</span>
                    {col.sortKey &&
                      (() => {
                        const idx = sortPriority.findIndex(s => s.key === col.sortKey)
                        if (idx === -1) return null
                        const dir = sortPriority[idx].order
                        return (
                          <>
                            <span className='text-gray dark:text-gray-400'>{dir === 'asc' ? '▲' : '▼'}</span>
                            <span className='text-gray dark:text-gray-400 text-[10px] ml-0.5'>{idx + 1}</span>
                          </>
                        )
                      })()}
                  </div>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columnConfig.length} className='py-10'>
                  <div className='flex justify-center'>
                    <LoadingIndicator />
                  </div>
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={columnConfig.length} className='px-4 py-6 text-red-600 dark:text-red-400'>
                  {error}
                </TableCell>
              </TableRow>
            ) : results.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columnConfig.length} className='px-4 py-6 text-gray-500 dark:text-gray-400'>
                  Sin resultados
                </TableCell>
              </TableRow>
            ) : (
              results.map(row => (
                <TableRow key={row.user_id} className='border-t dark:border-dk-border'>
                  {columnConfig.map(col => {
                    const key = col.key
                    if (key === 'user') {
                      return (
                        <TableCell
                          key={`${row.user_id}-${key}`}
                          className='px-4 py-3 whitespace-nowrap cursor-pointer hover:bg-gray-50 dark:hover:bg-dk-hover'
                          onClick={() =>
                            window.open(
                              `${window.location.origin
                              }?navigation=UserSearch&user_id=${encodeURIComponent(row.user_id)}`,
                              '_blank'
                            )
                          }
                        >
                          <div className='flex flex-col'>
                            <span
                              className={`font-semibold ${(row && (row.blocked || row.suspended)) ||
                                  row?.status === 'suspended'
                                  ? 'text-error'
                                  : ''
                                }`}
                            >
                              {row.name || row.user_id}
                              {(((row && (row.blocked || row.suspended)) ||
                                row?.status === 'suspended') && (
                                  <FontAwesomeIcon icon={faBan} className='ml-2 text-error' />
                                )) ||
                                null}
                            </span>
                            <span className='text-xs text-gray-500 dark:text-gray-400'>{row.user_id}</span>
                          </div>
                        </TableCell>
                      )
                    }
                    if (key === 'role') {
                      return (
                        <TableCell
                          key={`${row.user_id}-${key}`}
                          className='px-4 py-3 whitespace-nowrap capitalize'
                        >
                          {row.role}
                        </TableCell>
                      )
                    }
                    if (key === 'phone_number') {
                      return (
                        <TableCell
                          key={`${row.user_id}-${key}`}
                          className='px-4 py-3 whitespace-nowrap'
                        >
                          <div className='flex items-center gap-2'>
                            <span>{row.phone_number || '-'}</span>
                            {row.phone_number && (
                              <button
                                className='text-gray dark:text-gray-400 hover:text-primary'
                                onClick={() => navigator.clipboard.writeText(row.phone_number)}
                                title='Copiar teléfono'
                              >
                                <FontAwesomeIcon icon={faCopy} />
                              </button>
                            )}
                            {row.phone_number && onSendWhatsApp && (
                              <button
                                className='text-green-600 dark:text-emerald-400 hover:opacity-70'
                                onClick={() => onSendWhatsApp(row)}
                                title='Mandar WhatsApp'
                              >
                                <WhatsAppIcon fontSize='small' />
                              </button>
                            )}
                          </div>
                        </TableCell>
                      )
                    }
                    if (
                      key === 'conversion' ||
                      key === 'cancellation_rate' ||
                      key === 'abandonment_rate'
                    ) {
                      const v = row[key]
                      return (
                        <TableCell
                          key={`${row.user_id}-${key}`}
                          className='px-4 py-3 whitespace-nowrap'
                        >
                          {typeof v === 'number' ? v.toFixed(1) : v}
                        </TableCell>
                      )
                    }
                    if (key === 'rating') {
                      return (
                        <TableCell
                          key={`${row.user_id}-${key}`}
                          className='px-4 py-3 whitespace-nowrap'
                        >
                          {row.rating ?? '-'}
                        </TableCell>
                      )
                    }
                    // numeric default
                    return (
                      <TableCell
                        key={`${row.user_id}-${key}`}
                        className='px-4 py-3 whitespace-nowrap'
                      >
                        {row[key] === null || row[key] === undefined
                          ? '-'
                          : typeof row[key] === 'number'
                            ? numberFmt.format(row[key])
                            : row[key]}
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Footer / Pagination */}
      <div className='flex flex-col md:flex-row items-center justify-between gap-3 px-4 py-3 border-t dark:border-dk-border'>
        <div className='text-sm text-gray-600 dark:text-gray-300'>
          {total > 0 ? `Mostrando ${from}-${to} de ${numberFmt.format(total)}` : 'Sin resultados'}
        </div>
        <div className='flex items-center gap-2'>
          <button
            className='px-3 py-2 rounded-lg bg-light_gray dark:bg-dk-raised disabled:opacity-50'
            onClick={handlePrev}
            disabled={offset === 0 || loading}
          >
            Anterior
          </button>
          <button
            className='px-3 py-2 rounded-lg bg-light_gray dark:bg-dk-raised disabled:opacity-50'
            onClick={handleNext}
            disabled={offset + limit >= total || loading}
          >
            Siguiente
          </button>
        </div>
      </div>
    </div>
  )
}
